import Service from '@ember/service';
import {
  sub,
  eachDayOfInterval,
  add,
  max,
  min,
  startOfDay as pureStartOfDay
} from 'date-fns';
import { inject as service } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import { action } from '@ember/object';
import { isoDate } from '@dubbletrack/time/helpers/iso-date';
import _, { sortBy } from 'lodash';
import { tracked } from '@glimmer/tracking';
import { TrackedMap } from 'tracked-built-ins';
import { registerDestructor } from '@ember/destroyable';
import startOfDay from "@dubbletrack/time/utils/start-of-day";
import endOfDay from "@dubbletrack/time/utils/end-of-day";

export default class ScheduleDataStoreService extends Service {
  @service dataStore;
  @service store;
  @service socket;
  @service settings;
  @service clock;

  maxTime = endOfDay(add(new Date(), { weeks: 1 }));
  minTime = sub(new Date(), { weeks: 2 });
  airingIdMap = new TrackedMap();
  @tracked dates;
  @tracked dateObjects;
  @tracked _currentItem;

  constructor() {
    super(...arguments);
    this.dates = eachDayOfInterval({
      start: this.minTime,
      end: this.maxTime,
    }).map((a) => pureStartOfDay(a));


    this.dateObjects = this.dates.map((date) => {
      return {
        id: isoDate(date, { dateOnly: true }),
        date: date,
        airingIds: [],
      };
    });

    registerDestructor(this, () => {
      this.subscription?.unsubscribe();
      this.subscription = undefined;
    });
  }

  get currentItem() {
    if (this._currentItem) {
      return this._currentItem;
    }

    return this.dateObjects.find(
      (obj) => obj.id == isoDate(pureStartOfDay(new Date()), { dateOnly: true }),
    )
  }
  set currentItem(value) {
    this._currentItem = value;
  }

  loadAiringsFromDateTask = task(
    { maxConcurrency: 10, restartable: true },
    async (dateObject, reload = false) => {
      if (this.airingIdMap.get(dateObject.id) && !reload) {
        return; // already called
      }

      let airings = await this.dataStore.loadAiringsTask.perform(
        startOfDay(dateObject.date, { timeZone: this.clock.timeZone }),
        endOfDay(dateObject.date, { timeZone: this.clock.timeZone }),
      );
      this.airingIdMap.set(
        dateObject.id,
        airings.map((a) => a.id),
      );
      dateObject.isLoaded = true;
      this.loadCableTask.perform();
    }
  );

  loadCableTask = task({ maxConcurrency: 1, restartable: true }, async () => {
    await timeout(300);

    let cachedDateKeys = [...this.airingIdMap.keys()];
    let relevantDateObjects = [...this.dateObjects].filter((dateObj) =>
      cachedDateKeys.includes(dateObj.id),
    );

    let earliestDate = min(relevantDateObjects.map((item) => startOfDay(item.date, { timeZone: this.clock.timeZone })));
    let latestDate = max(relevantDateObjects.map((item) => endOfDay(item.date, { timeZone: this.clock.timeZone })));

    this.subscription?.unsubscribe();
    this.subscription = this.socket.subscribe(
      {
        channel: 'SignalChannel',
        station_id: this.settings.stationId,
        iso_start_date: earliestDate.toISOString(),
        iso_end_date: latestDate.toISOString(),
      },
      {
        received: async (payload) => {
          // debugger
          if (this.settings.debug) {
            console.log(`⚡️ schedule day received cable update`, payload);
          }

          let dateObject = [...this.dateObjects].find(
            (dateObj) => dateObj.id == payload.date,
          );
          if (dateObject) {
            this.dataStore.loadAiringsTask.perform(
              startOfDay(dateObject.date, { timeZone: this.clock.timeZone }),
              endOfDay(dateObject.date, { timeZone: this.clock.timeZone }),
            );
          }
        },
      },
    );
  });

  @action
  isDayLoaded(dateObject) {
    return this.airingIdMap.get(dateObject.id)
  }
}
